import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Save, GripVertical, X, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const EMPTY = { slug: "", name: { id: "", en: "" }, order: 0 };

export default function CategoriesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, slug, name, order}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dragIndex, setDragIndex] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/categories");
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing({ ...EMPTY, order: items.length });
    setError("");
  };

  const startEdit = (cat) => {
    setEditing({ ...cat });
    setError("");
  };

  const cancel = () => {
    setEditing(null);
    setError("");
  };

  const save = async () => {
    if (!editing.name.id?.trim() && !editing.name.en?.trim()) {
      setError("Name is required (Indonesian or English).");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editing.id) {
        await api.put(`/categories/${editing.id}`, editing);
      } else {
        await api.post("/categories", editing);
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await api.delete(`/categories/${id}`);
      load();
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete");
    }
  };

  const onDragStart = (i) => setDragIndex(i);
  const onDragOver = (e, i) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) return;
    const next = [...items];
    const [m] = next.splice(dragIndex, 1);
    next.splice(i, 0, m);
    setItems(next);
    setDragIndex(i);
  };
  const onDragEnd = async () => {
    if (dragIndex === null) return;
    setDragIndex(null);
    try {
      await api.post("/categories/reorder", { ids: items.map((c) => c.id) });
    } catch {
      load();
    }
  };

  return (
    <div className="p-10 max-w-[1080px]" data-testid="admin-categories">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#5d5f64] mb-2">Taxonomy</div>
          <h1 className="font-display font-semibold text-[#16181c] text-3xl tracking-[-0.02em]">
            Kategori proyek · Project categories
          </h1>
          <p className="text-sm text-[#5d5f64] mt-2 max-w-[60ch]">
            These are the filter chips on the homepage and projects index, and the options in the
            project type dropdown. Drag to reorder. Renaming keeps existing projects linked
            automatically (the slug is rewritten in the projects collection).
          </p>
        </div>
        <button
          type="button"
          onClick={startNew}
          className="inline-flex items-center gap-2 bg-[#16181c] text-white px-5 py-3 font-medium hover:bg-[#5d5f64]"
          data-testid="admin-new-category"
        >
          <Plus size={16} /> New category
        </button>
      </div>

      {loading ? (
        <div className="text-[#5d5f64]">Loading…</div>
      ) : (
        <div className="bg-white border border-[#e3e3e1]" data-testid="categories-table">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#e3e3e1] text-xs uppercase tracking-wider text-[#5d5f64] items-center">
            <div className="col-span-1"></div>
            <div className="col-span-4">Name (ID)</div>
            <div className="col-span-4">Name (EN)</div>
            <div className="col-span-2">Slug</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {items.map((c, i) => (
            <div
              key={c.id}
              draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={(e) => onDragOver(e, i)}
              onDragEnd={onDragEnd}
              className={`grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#e3e3e1] items-center ${
                dragIndex === i ? "bg-[#f3f3f0]" : ""
              }`}
              data-testid={`category-row-${c.id}`}
            >
              <div className="col-span-1 text-[#5d5f64] cursor-grab active:cursor-grabbing">
                <GripVertical size={16} />
              </div>
              <div className="col-span-4 font-medium text-[#16181c]">{c.name?.id || "—"}</div>
              <div className="col-span-4 text-[#16181c]">{c.name?.en || "—"}</div>
              <div className="col-span-2 text-xs text-[#5d5f64] font-mono truncate">/{c.slug}</div>
              <div className="col-span-1 flex justify-end gap-3">
                <button
                  onClick={() => startEdit(c)}
                  className="text-xs text-[#16181c] hover:text-[#5d5f64] border-b border-[#16181c]"
                  data-testid={`category-edit-${c.id}`}
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(c.id)}
                  className="text-[#16181c] hover:text-[#5d5f64]"
                  data-testid={`category-delete-${c.id}`}
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="px-6 py-10 text-center text-[#5d5f64]">No categories yet.</div>
          )}
        </div>
      )}

      {editing && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 bg-[#1b1c1e]/60 flex items-center justify-center p-4"
          onClick={cancel}
        >
          <div
            className="bg-white max-w-[520px] w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="category-form"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#e3e3e1]">
              <div className="font-display font-semibold text-[#16181c] text-lg">
                {editing.id ? "Edit category" : "New category"}
              </div>
              <button
                onClick={cancel}
                className="p-1 text-[#5d5f64] hover:text-[#16181c]"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="block text-xs font-medium text-[#5d5f64] mb-2">Name (Indonesian)</span>
                <input
                  type="text"
                  className="cat-input"
                  value={editing.name.id || ""}
                  onChange={(e) => setEditing({ ...editing, name: { ...editing.name, id: e.target.value } })}
                  data-testid="category-name-id"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-[#5d5f64] mb-2">Name (English)</span>
                <input
                  type="text"
                  className="cat-input"
                  value={editing.name.en || ""}
                  onChange={(e) => setEditing({ ...editing, name: { ...editing.name, en: e.target.value } })}
                  data-testid="category-name-en"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-[#5d5f64] mb-2">
                  Slug (auto-generated if blank; lower-case, hyphenated)
                </span>
                <input
                  type="text"
                  className="cat-input font-mono"
                  value={editing.slug || ""}
                  onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                  placeholder="e.g. pendidikan"
                  data-testid="category-slug"
                />
              </label>
              {error && (
                <div className="text-sm text-[#16181c] bg-[#f3f3f0] border border-[#e3e3e1] px-3 py-2" data-testid="category-error">
                  {error}
                </div>
              )}
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="inline-flex items-center gap-2 bg-[#16181c] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#5d5f64] disabled:opacity-60"
                  data-testid="category-save"
                >
                  {saving ? "Saving…" : (<><Check size={14} /> Save</>)}
                </button>
                <button
                  onClick={cancel}
                  className="text-sm text-[#5d5f64] hover:text-[#16181c]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 text-xs text-[#5d5f64]">
        <Link to="/admin" className="hover:text-[#16181c]">← Back to dashboard</Link>
      </div>

      <style>{`
        .cat-input {
          width: 100%;
          background: #ffffff;
          border: 1px solid #e3e3e1;
          padding: 10px 12px;
          font-family: "Inter", sans-serif;
          font-size: 14px;
          color: #16181c;
        }
        .cat-input:focus { outline: none; border-color: #16181c; }
      `}</style>
    </div>
  );
}
